<template>
  <div class="transaction-item d-flex justify-content-between align-items-center gap-3">
    <div class="d-flex align-items-center gap-3 min-w-0">
      <div
        class="transaction-icon-wrapper"
        :style="iconWrapperStyle"
      >
        <template v-if="hasImageCategoryIcon">
          <img
            :src="transaction.category_icon"
            :alt="transaction.category_name || transaction.description"
            class="transaction-icon-image"
          />
        </template>

        <template v-else-if="hasClassCategoryIcon">
          <i :class="'fas '+ transaction.category_icon"></i>
        </template>

        <template v-else>
          <i class="fal fa-receipt"></i>
        </template>
      </div>

      <div class="transaction-info min-w-0">
        <div class="transaction-top d-flex align-items-center gap-2 flex-wrap">
          <small class="transaction-date">{{ formattedDate }}</small>

          <span
            v-if="statusLabel"
            :class="['transaction-status', statusTone]"
          >
            {{ statusLabel }}
          </span>
        </div>

        <p class="transaction-title mb-0 text-truncate">
          {{ transaction.description }}
          <span class="text-muted fs-10 mt-n2" v-if="(transaction.customer_suplier_id > 0)">
            <br>
            para {{ transaction.customer_supplier_name }}
          </span>
        </p>
      </div>
    </div>

    <div class="text-end transaction-right">
      <p :class="['transaction-amount mb-2', amountTone]">
        {{ formattedAmount }}
      </p>

      <button
        v-if="showActionButton"
        class="action-btn"
        type="button"
        @click="emit('pay', transaction)"
      >
        {{ actionLabel }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  transaction: {
    type: Object,
    required: true,
  },
  darkmode: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const emit = defineEmits(["pay"]);

const normalizeType = computed(() => {
  const type = String(props.transaction?.type || "").toLowerCase();

  if (type.includes("desp")) return "expense";
  if (type.includes("rece")) return "revenue";
  if (type.includes("transfer")) return "transfer";
  return type;
});

const isExpense = computed(() => normalizeType.value === "expense");
const isRevenue = computed(() => normalizeType.value === "revenue");

const amountNumber = computed(() => Number(props.transaction?.amount || 0));

const formattedAmount = computed(() =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amountNumber.value)
);

const formattedDate = computed(() => {
  const raw = props.transaction?.date;
  if (!raw) return "";

  const parsed = new Date(raw + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return raw;

  return new Intl.DateTimeFormat(t('message.global.date_language')).format(parsed);
});

const amountTone = computed(() => {
  if (isExpense.value) return "neg";
  if (isRevenue.value) return "pos";
  return "neutral";
});

const statusLabel = computed(() => {
  const status = String(props.transaction?.status || "").toLowerCase();

  if (status === "paid") return "Pago";
  if (status === "unpaid") return "Pendente";
  if (status === "overdue") return "Em atraso";

  if (props.transaction?.date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const txDate = new Date(props.transaction.date + "T00:00:00");
    if (!Number.isNaN(txDate.getTime()) && status !== "paid" && txDate < today) {
      return "Em atraso";
    }
  }

  return "";
});

const statusTone = computed(() => {
  if (statusLabel.value === "Em atraso") return "status-overdue";
  if (statusLabel.value === "Pago") return "status-paid";
  return "status-pending";
});

const showActionButton = computed(() => {
  return String(props.transaction?.status || "").toLowerCase() !== "paid";
});

const actionLabel = computed(() => {
  return isRevenue.value ? "Receber" : "Pagar";
});

const hasImageCategoryIcon = computed(() => {
  const icon = props.transaction?.category_icon;
  if (!icon) return false;

  return (
    icon.startsWith("http") ||
    icon.startsWith("/") ||
    icon.startsWith("data:image") ||
    /\.(png|jpg|jpeg|svg|webp)$/i.test(icon)
  );
});

const hasClassCategoryIcon = computed(() => {
  const icon = props.transaction?.category_icon;
  if (!icon) return false;
  return !hasImageCategoryIcon.value;
});

const iconWrapperStyle = computed(() => {
  const color = props.transaction?.category_color || "#12395f";

  return {
    background: color + "22",
    color: color,
  };
});
</script>

<style scoped>
.min-w-0 {
  min-width: 0;
}

.transaction-item {
  background: transparent;
  border: 0;
  border-radius: 18px;
  padding: 16px 14px;
  transition: background-color 0.2s ease, transform 0.2s ease;
  margin-bottom: 8px;
}

.transaction-item:hover {
  background: rgba(18, 57, 95, 0.035);
  transform: translateY(-1px);
}

.transaction-icon-wrapper {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  backdrop-filter: blur(6px);
}

.transaction-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 14px;
}

.transaction-icon-wrapper i {
  font-size: 18px;
}

.transaction-info {
  overflow: hidden;
}

.transaction-top {
  margin-bottom: 4px;
}

.transaction-date {
  font-size: 12px;
  color: #627693;
}

.transaction-status {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 8px;
}

.status-overdue {
  background: rgba(245, 54, 92, 0.12);
  color: #c9485b;
}

.status-paid {
  background: rgba(45, 206, 137, 0.14);
  color: #198754;
}

.status-pending {
  background: rgba(18, 57, 95, 0.08);
  color: #12395f;
}

.transaction-title {
  font-size: 15px;
  font-weight: 800;
  color: #31486d;
}

.transaction-right {
  min-width: 120px;
}

.transaction-amount {
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
}

.transaction-amount.neg {
  color: #c9485b;
}

.transaction-amount.pos {
  color: #198754;
}

.transaction-amount.neutral {
  color: #17212f;
}

.action-btn {
  font-size: 12px;
  font-weight: 700;
  border: none;
  background: #12395f;
  color: #ffffff;
  padding: 7px 12px;
  border-radius: 999px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #0f2f4e;
}
</style>